/**
 * URL Parser Utility
 * Functions for parsing video URLs from supported platforms
 */

export type Platform = 'kick' | 'youtube' | 'twitch' | 'apple-podcasts' | 'gettr' | 'unknown';

export interface ParsedUrl {
  platform: Platform;
  videoId: string | null;
  channelName: string | null;
  isValid: boolean;
}

export interface KickVideoIdentifiers {
  streamerName: string;
  vodUuid: string;
}

/**
 * Detect the streaming platform from a URL
 */
export function detectPlatform(url: string): Platform {
  try {
    const parsed = new URL(url);
    const hostname = parsed.hostname.toLowerCase();

    if (hostname.includes('kick.com')) {
      return 'kick';
    }
    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return 'youtube';
    }
    if (hostname.includes('twitch.tv')) {
      return 'twitch';
    }
    if (hostname.includes('podcasts.apple.com')) {
      return 'apple-podcasts';
    }
    if (hostname.includes('gettr.com')) {
      return 'gettr';
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Parse a Kick.com video URL to extract identifiers
 * Supports formats:
 * - https://kick.com/video/UUID
 * - https://kick.com/CHANNEL/videos/UUID
 */
export function parseKickVideoIdentifiers(url: string): KickVideoIdentifiers | null {
  try {
    const parsed = new URL(url);
    const pathParts = parsed.pathname.split('/').filter(Boolean);

    // Format: /video/UUID
    if (pathParts[0] === 'video' && pathParts[1]) {
      return {
        streamerName: '', // Not available in this format
        vodUuid: pathParts[1],
      };
    }

    // Format: /CHANNEL/videos/UUID
    if (pathParts.length >= 3 && pathParts[1] === 'videos' && pathParts[2]) {
      return {
        streamerName: pathParts[0],
        vodUuid: pathParts[2],
      };
    }

    // Format: /CHANNEL (live stream)
    if (pathParts.length === 1) {
      return {
        streamerName: pathParts[0],
        vodUuid: '',
      };
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Parse a YouTube URL to extract video ID
 * Supports formats:
 * - https://youtube.com/watch?v=VIDEO_ID
 * - https://youtu.be/VIDEO_ID
 * - https://youtube.com/embed/VIDEO_ID
 */
export function parseYouTubeVideoId(url: string): string | null {
  try {
    const parsed = new URL(url);

    // Handle youtu.be short URLs
    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.slice(1) || null;
    }

    // Handle youtube.com/watch?v=
    const videoId = parsed.searchParams.get('v');
    if (videoId) {
      return videoId;
    }

    // Handle youtube.com/embed/VIDEO_ID
    const pathParts = parsed.pathname.split('/').filter(Boolean);
    if (pathParts[0] === 'embed' && pathParts[1]) {
      return pathParts[1];
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Parse a Twitch URL to extract VOD ID or channel name
 * Supports formats:
 * - https://twitch.tv/videos/VOD_ID
 * - https://twitch.tv/CHANNEL
 */
export function parseTwitchIdentifiers(url: string): { vodId: string | null; channelName: string | null } {
  try {
    const parsed = new URL(url);
    const pathParts = parsed.pathname.split('/').filter(Boolean);

    // Format: /videos/VOD_ID
    if (pathParts[0] === 'videos' && pathParts[1]) {
      return {
        vodId: pathParts[1],
        channelName: null,
      };
    }

    // Format: /CHANNEL
    if (pathParts.length === 1) {
      return {
        vodId: null,
        channelName: pathParts[0],
      };
    }

    return { vodId: null, channelName: null };
  } catch {
    return { vodId: null, channelName: null };
  }
}

/**
 * Parse any supported video URL
 */
export function parseVideoUrl(url: string): ParsedUrl {
  const platform = detectPlatform(url);

  if (platform === 'unknown') {
    return {
      platform: 'unknown',
      videoId: null,
      channelName: null,
      isValid: false,
    };
  }

  switch (platform) {
    case 'kick': {
      const kick = parseKickVideoIdentifiers(url);
      return {
        platform: 'kick',
        videoId: kick?.vodUuid || null,
        channelName: kick?.streamerName || null,
        isValid: !!kick,
      };
    }
    case 'youtube': {
      const videoId = parseYouTubeVideoId(url);
      return {
        platform: 'youtube',
        videoId,
        channelName: null,
        isValid: !!videoId,
      };
    }
    case 'twitch': {
      const twitch = parseTwitchIdentifiers(url);
      return {
        platform: 'twitch',
        videoId: twitch.vodId,
        channelName: twitch.channelName,
        isValid: !!(twitch.vodId || twitch.channelName),
      };
    }
    case 'apple-podcasts': {
      // Apple Podcasts URL format: https://podcasts.apple.com/us/podcast/episode-name/id123456?i=1000123456
      try {
        const parsed = new URL(url);
        const pathParts = parsed.pathname.split('/').filter(Boolean);
        // Extract episode ID from query param 'i' or from the path
        const episodeId = parsed.searchParams.get('i') || null;
        // Podcast name is usually the 3rd path segment (after locale and "podcast")
        const podcastName = pathParts.length >= 3 ? pathParts[2] : null;
        return {
          platform: 'apple-podcasts',
          videoId: episodeId,
          channelName: podcastName,
          isValid: true,
        };
      } catch {
        return {
          platform: 'apple-podcasts',
          videoId: null,
          channelName: null,
          isValid: true,
        };
      }
    }
    case 'gettr': {
      // GetTR URLs have no standardized video-ID structure we parse;
      // treat any gettr.com URL as valid and let the workflow resolve it.
      return {
        platform: 'gettr',
        videoId: null,
        channelName: null,
        isValid: true,
      };
    }
    default:
      return {
        platform: 'unknown',
        videoId: null,
        channelName: null,
        isValid: false,
      };
  }
}

/**
 * Get platform display name
 */
export function getPlatformDisplayName(platform: Platform): string {
  switch (platform) {
    case 'kick':
      return 'Kick';
    case 'youtube':
      return 'YouTube';
    case 'twitch':
      return 'Twitch';
    case 'apple-podcasts':
      return 'Apple Podcasts';
    case 'gettr':
      return 'GetTR';
    default:
      return 'Unknown';
  }
}
